// Shared utilities for betting system

interface PlayerStats {
  id: string;
  name: string;
  teamId: string;
  avgPoints: number;
  avgAssists: number;
  gamesPlayed: number;
}

interface RostersData {
  [teamId: string]: PlayerStats[];
}

interface SelectedPlayers {
  team1Points: PlayerStats | null;
  team1Assists: PlayerStats | null;
  team2Points: PlayerStats | null;
  team2Assists: PlayerStats | null;
}

// Seeded random number generator
class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = seed;
  }

  next(): number {
    this.seed = (this.seed * 9301 + 49297) % 233280;
    return this.seed / 233280;
  }

  between(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }
}

// Calculate team average points from standings
export const getTeamAveragePoints = (standing: any): number => {
  if (!standing) return 0;
  const totalGames = standing.wins + standing.losses;
  if (totalGames === 0) return 0;
  return Math.round(standing.points_for / totalGames);
};

// Select random players using game seed (same logic as BettingPanel)
export const selectPlayers = (
  gameId: string,
  team1Players: PlayerStats[],
  team2Players: PlayerStats[]
): SelectedPlayers => {
  // Create seed from gameId
  let seed = 0;
  for (let i = 0; i < gameId.length; i++) {
    seed = ((seed << 5) - seed) + gameId.charCodeAt(i);
    seed = seed & seed; // Convert to 32bit integer
  }
  seed = Math.abs(seed);
  
  const random = new SeededRandom(seed);
  
  // Filter players with stats
  const team1WithStats = team1Players.filter(p => p.gamesPlayed > 0);
  const team2WithStats = team2Players.filter(p => p.gamesPlayed > 0);
  
  // Select 1 player for points and 1 for assists from each team
  let team1PointsPlayer: PlayerStats | null = null;
  let team1AssistsPlayer: PlayerStats | null = null;
  
  if (team1WithStats.length > 0) {
    const pointsIdx = random.between(0, team1WithStats.length - 1);
    team1PointsPlayer = team1WithStats[pointsIdx];
    
    if (team1WithStats.length > 1) {
      const remaining = team1WithStats.filter((_, idx) => idx !== pointsIdx);
      const assistsIdx = random.between(0, remaining.length - 1);
      team1AssistsPlayer = remaining[assistsIdx];
    }
  }
  
  let team2PointsPlayer: PlayerStats | null = null;
  let team2AssistsPlayer: PlayerStats | null = null;
  
  if (team2WithStats.length > 0) {
    const pointsIdx = random.between(0, team2WithStats.length - 1);
    team2PointsPlayer = team2WithStats[pointsIdx];
    
    if (team2WithStats.length > 1) {
      const remaining = team2WithStats.filter((_, idx) => idx !== pointsIdx);
      const assistsIdx = random.between(0, remaining.length - 1);
      team2AssistsPlayer = remaining[assistsIdx];
    }
  }
  
  return {
    team1Points: team1PointsPlayer,
    team1Assists: team1AssistsPlayer,
    team2Points: team2PointsPlayer,
    team2Assists: team2AssistsPlayer
  };
};

